import { useEffect, useRef } from 'react';

interface MediaLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  mediaUrl: string;
  mediaType: 'image' | 'video';
  caption?: string;
}

export default function MediaLightbox({
  isOpen,
  onClose,
  mediaUrl,
  mediaType,
  caption,
}: MediaLightboxProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Store the trigger element when lightbox opens
  useEffect(() => {
    if (isOpen && !triggerRef.current) {
      triggerRef.current = document.activeElement as HTMLElement;
    }
  }, [isOpen]);

  // Focus close button when lightbox opens
  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isOpen]);

  // Keyboard trap for lightbox
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      if (e.key === 'Tab') {
        e.preventDefault();
        
        const focusableElements = Array.from(
          document.querySelectorAll('.lightbox-focusable')
        ) as HTMLElement[];
        
        if (focusableElements.length === 0) return;

        const currentIndex = Array.from(focusableElements).indexOf(
          document.activeElement as HTMLElement
        );

        let nextIndex: number;
        if (e.shiftKey) {
          // Shift+Tab: move backwards
          nextIndex = currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
        } else {
          // Tab: move forwards
          nextIndex = currentIndex >= focusableElements.length - 1 ? 0 : currentIndex + 1;
        }

        focusableElements[nextIndex]?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleClose = () => {
    onClose();
    // Return focus to the element that opened the lightbox
    if (triggerRef.current) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Media viewer"
      onClick={handleClose}
    >
      {/* Close Button */}
      <button
        ref={closeButtonRef}
        onClick={handleClose}
        className="lightbox-focusable absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors z-10 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-black"
        aria-label="Close media viewer"
      >
        <i className="ri-close-line text-2xl text-white"></i>
      </button>

      {/* Media Content */}
      <div className="max-w-7xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
        {mediaType === 'image' ? (
          <img
            src={mediaUrl}
            alt={caption || 'Review media'}
            className="max-w-full max-h-[90vh] object-contain mx-auto"
          />
        ) : (
          <video
            src={mediaUrl}
            controls
            className="max-w-full max-h-[90vh] mx-auto focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-black"
            aria-label={caption || 'Review video'}
          >
            <track kind="captions" />
          </video>
        )}

        {/* Caption */}
        {caption && (
          <div className="mt-4 text-center">
            <p className="text-white text-sm">{caption}</p>
          </div>
        )}
      </div>
    </div>
  );
}
